import { DateTime } from "luxon";
import Appointment, { FollowUpType } from "models/appointment";
import { DEFAULT_TIMEZONE } from "utils/constants";
import { UserLanguage } from "utils/types";

export const ServiceType = {
    public: "public",
    private: "private",
} as const;

type ServiceType = typeof ServiceType[keyof typeof ServiceType];

const RESULTS_DELAY_REGULAR = 10;
const RESULTS_DELAY_EXPRESS = 5;
const RESULTS_DELAY_PRIVATE = 2;
const RESULTS_DELAY_PRIVATE_INCLUDING_SUNDAY = 3;

export const getEstimatedResultsDateForAppointment = (
    appointment: Appointment,
    serviceType: ServiceType,
    language: UserLanguage
): string => {
    const appointmentDatetime = DateTime.fromISO(appointment.datetime, {
        zone: DEFAULT_TIMEZONE,
    });

    const delayDays = getDelayDays(
        appointmentDatetime,
        serviceType,
        appointment.followUpType
    );

    return appointmentDatetime
        .plus({ days: delayDays })
        .setLocale(language)
        .toLocaleString({ month: "long", day: "numeric", year: "numeric" });
};

const getDelayDays = (
    datetime: DateTime,
    serviceType: ServiceType,
    followUpType?: FollowUpType
): number => {
    if (serviceType === ServiceType.private) {
        const isFriday = datetime.weekday === 5;
        return isFriday
            ? RESULTS_DELAY_PRIVATE_INCLUDING_SUNDAY
            : RESULTS_DELAY_PRIVATE;
    }

    return followUpType === FollowUpType.Express
        ? RESULTS_DELAY_EXPRESS
        : RESULTS_DELAY_REGULAR;
};

export const getLocalizedAppointmentDateWithTime = (
    appointment: Appointment,
    language: UserLanguage
): string => {
    const appointmentDateTime = DateTime.fromISO(appointment.datetime, {
        zone: DEFAULT_TIMEZONE,
    });

    return appointmentDateTime.setLocale(language).toLocaleString({
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

/** Booking-flow confirmation: "August 18, 2026 • 8:30 AM" (no "Appointment" label). */
export const getBookingFlowConfirmationDateTime = (
    appointment: Appointment,
    language: UserLanguage
): string => {
    const appointmentDateTime = DateTime.fromISO(appointment.datetime, {
        zone: DEFAULT_TIMEZONE,
    }).setLocale(language);

    const datePart = appointmentDateTime.toLocaleString({
        year: "numeric",
        month: "long",
        day: "numeric",
    });
    const timePart = appointmentDateTime.toLocaleString({
        hour: "numeric",
        minute: "2-digit",
    });

    return `${datePart} • ${timePart}`;
};
